import { type DettaglioFirma } from '@framework/api-client/api/inquiries-compile/schemas/dettaglioFirma.ts';
import { EnumTipoDomandaDTOCaricamentoPdfRiepilogoModalitaFirma } from '@framework/api-client/api/inquiries-compile/schemas/enumTipoDomandaDTOCaricamentoPdfRiepilogoModalitaFirma.ts';
import { EnumTipoDomandaDTOModalitaRiepilogo } from '@framework/api-client/api/inquiries-compile/schemas/enumTipoDomandaDTOModalitaRiepilogo.ts';
import { EnumTipoDomandaDTOProfiloAgid } from '@framework/api-client/api/inquiries-compile/schemas/enumTipoDomandaDTOProfiloAgid.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoDomandaDTO.ts';
import { checkNever } from '@framework/engine/utils/checkNever.ts';
import { SignatureFormat } from '../signer/signature/SignatureFormat.ts';

export class SubmissionSummaryLogic {
  /**
   * Restituisce `true` se la sezione riepilogo esteso è attiva
   * (disponibile solo con `modalitaRiepilogo === ESTESO`).
   */
  static isExtendedSummaryEnabled(
    inquiryType?: TipoDomandaDTO | null,
  ): boolean {
    return (
      inquiryType?.modalitaRiepilogo ===
      EnumTipoDomandaDTOModalitaRiepilogo.ESTESO
    );
  }

  /**
   * Restituisce `true` se l'upload del PDF firmato è abilitato.
   * Richiede `modalitaRiepilogo === ESTESO` e `flAbilitaCaricamentoPdfRiepilogo === true`.
   */
  static isSignedSummaryUploadEnabled(
    inquiryType?: TipoDomandaDTO | null,
  ): boolean {
    return (
      SubmissionSummaryLogic.isExtendedSummaryEnabled(inquiryType) &&
      inquiryType?.flAbilitaCaricamentoPdfRiepilogo === true
    );
  }

  /**
   * Restituisce la stringa `accept` per l'input file di upload del riepilogo firmato,
   * in base alla modalità di firma configurata.
   *
   * - `DIGITALE` → `.pdf,.p7m` (PAdES o CAdES)
   * - `DIGITALE_O_AUTOGRAFA` → `null` (nessun filtro, tutti i formati)
   * - `SOLO_PADES` → `.pdf`
   * - `SOLO_CADES` → `.p7m`
   */
  static getAcceptedFileExtensions(
    inquiryType?: TipoDomandaDTO | null,
  ): string | null {
    const mode = inquiryType?.caricamentoPdfRiepilogoModalitaFirma;
    if (mode == null) return '.pdf,.p7m';

    switch (mode) {
      case EnumTipoDomandaDTOCaricamentoPdfRiepilogoModalitaFirma.SOLO_PADES:
        return '.pdf';
      case EnumTipoDomandaDTOCaricamentoPdfRiepilogoModalitaFirma.SOLO_CADES:
        return '.p7m';
      case EnumTipoDomandaDTOCaricamentoPdfRiepilogoModalitaFirma.DIGITALE_O_AUTOGRAFA:
        return null;
      case EnumTipoDomandaDTOCaricamentoPdfRiepilogoModalitaFirma.DIGITALE:
        return '.pdf,.p7m';
      default:
        checkNever(mode);
        return null;
    }
  }

  /**
   * Risolve i dettagli firma da mostrare nella UI.
   *
   * Quando il backend non popola `riepilogoDettagliFirma` (array vuoto/null) ma
   * è stato caricato un file firmato e la modalità configurata permette la
   * firma autografa (`DIGITALE_O_AUTOGRAFA`), sintetizziamo un `DettaglioFirma`
   * con `formato: AUTOGRAFA` così che la modal "Verifica Firma" mostri il caso
   * autografa. Nelle altre modalità un array vuoto è un'anomalia e non viene
   * mascherata.
   */
  static resolveSignatures(params: {
    inquiryType?: TipoDomandaDTO | null;
    hasSignedSummary: boolean;
    signatures?: DettaglioFirma[] | null;
  }): DettaglioFirma[] | undefined {
    const { inquiryType, hasSignedSummary, signatures } = params;
    if (signatures && signatures.length > 0) return signatures;
    if (!hasSignedSummary) return signatures ?? undefined;

    const mode = inquiryType?.caricamentoPdfRiepilogoModalitaFirma;
    if (
      mode ===
      EnumTipoDomandaDTOCaricamentoPdfRiepilogoModalitaFirma.DIGITALE_O_AUTOGRAFA
    ) {
      return [{ formato: SignatureFormat.Handwritten }];
    }
    return signatures ?? undefined;
  }

  /**
   * Restituisce `true` se la validazione formale dell'istanza è richiesta.
   * Attiva solo con `modalitaRiepilogo === ESTESO` e `profiloAgid === AGID_SUAP`.
   */
  static isValidationRequired(inquiryType?: TipoDomandaDTO | null): boolean {
    return (
      SubmissionSummaryLogic.isExtendedSummaryEnabled(inquiryType) &&
      inquiryType?.profiloAgid === EnumTipoDomandaDTOProfiloAgid.AGID_SUAP
    );
  }
}
